import type { IdentifierScheme } from '@/api/types';

/**
 * The IDENTIFIER and BANK_DETAILS format rules, mirrored from the server's `DataroomFormats.java`
 * (OL-22 slice 2). The room checks as the applicant types so a typo is caught before submit; the
 * server re-checks, and is the authority. Both suites run the same vectors
 * (`product-api/src/test/resources/dataroom/format-vectors.json`), so the rules cannot drift.
 */
export type FormatCode = 'REQUIRED' | 'FORMAT' | 'CHECKSUM';

const SHAPES: Record<IdentifierScheme, RegExp> = {
  PAN: /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/,
  GSTIN: /^(0[1-9]|[1-3][0-9])[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  CIN: /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
  UDYAM: /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/,
  TAN: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
  IEC: /^[A-Z0-9]{10}$/,
  VOTER: /^[A-Z]{3}[0-9]{7}$/,
  DL: /^[A-Z]{2}[0-9]{13}$/,
  PASSPORT: /^[A-Z][0-9]{7}$/,
  WALLET: /^(0x[a-fA-F0-9]{40}|[13][a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[a-z0-9]{8,87})$/,
};

const BASE36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function normalizeIdentifier(scheme: IdentifierScheme, raw: string): string {
  const v = (raw ?? '').trim();
  if (scheme === 'WALLET') return v;
  const up = v.toUpperCase().replace(/ /g, '');
  return scheme === 'DL' ? up.replace(/-/g, '') : up;
}

export function gstinCheckChar(gstin: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const product = BASE36.indexOf(gstin[i]) * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return BASE36[(36 - (sum % 36)) % 36];
}

const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export const normalizeAadhaar = (raw: string): string => (raw ?? '').replace(/[ -]/g, '');

/** `null` when `normalized` is 12 digits, not starting 0 or 1, with UIDAI's Verhoeff check digit. */
export function checkAadhaar(normalized: string): FormatCode | null {
  if (!normalized) return 'REQUIRED';
  if (!/^[2-9][0-9]{11}$/.test(normalized)) return 'FORMAT';
  let c = 0;
  for (let i = 0; i < normalized.length; i++) {
    const d = normalized.charCodeAt(normalized.length - 1 - i) - 48;
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][d]];
  }
  return c === 0 ? null : 'CHECKSUM';
}

/** `null` when `normalized` is a well-formed identifier of `scheme`. */
export function checkIdentifier(scheme: IdentifierScheme, normalized: string): FormatCode | null {
  if (!normalized) return 'REQUIRED';
  if (!SHAPES[scheme].test(normalized)) return 'FORMAT';
  if (scheme === 'GSTIN' && gstinCheckChar(normalized) !== normalized[14]) return 'CHECKSUM';
  return null;
}

function shape(re: RegExp, v: string): FormatCode | null {
  if (!v) return 'REQUIRED';
  return re.test(v) ? null : 'FORMAT';
}

export const checkIfsc = (v: string) => shape(/^[A-Z]{4}0[A-Z0-9]{6}$/, v);
export const checkAccountNumber = (v: string) => shape(/^[0-9]{9,18}$/, v);
export const checkVpa = (v: string) => shape(/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/, v);
